import {
  CSSProperties,
  FC,
  memo,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { CountdownTimerContext } from "../../../components";
import { MatchStatus } from "../../../service";
import { getTeamColors } from "../../../components/colors";
import { Text } from "components/common/Text";
import { TwoTeamScoreBoard } from "../../../components/common";

const TIMER_PAUSE_EVENTS: string[] = [
  "1287", // Timeout
  "1271", // Penalty
  "1275", // Penalty Shot
  "1276", // Penalty Shot Missed
  "1280", // End of 2nd Period
  "1282", // Overtime End
  "1283", // Penalty Shootout
];

const TIMER_HIDE_EVENTS: string[] = [
  "1280", // End of 2nd Period
  "1282", // Overtime End
  "1283", // Penalty Shootout
];

export const Timer: FC<{ status: MatchStatus }> = memo(({ status }) => {
  const { nsc, c, tm } = status as any;
  const [homeScore, awayScore] = useMemo((): [number, number] => {
    const safePair = (arr: any): [number, number] =>
      Array.isArray(arr) && arr.length >= 2
        ? [Number(arr[0]) || 0, Number(arr[1]) || 0]
        : [0, 0];

    if (Array.isArray(c?.T)) return safePair(c.T);

    const parts = [c?.P1, c?.P2, c?.P3, c?.OT, c?.SO];
    return parts.reduce<[number, number]>(
      (acc, cur) => {
        const [a, b] = safePair(cur);
        return [acc[0] + a, acc[1] + b];
      },
      [0, 0]
    );
  }, [c]);
  const [isOvertime, setIsOvertime] = useState(false);
  const { minutes, seconds, pauseTimer, continueTimer, setDirection } =
    useContext(CountdownTimerContext);

  useEffect(() => {
    if (tm > 0) setDirection(+1);
    else setDirection(-1);
  }, [setDirection, tm]);

  useEffect(() => {
    if (nsc && TIMER_PAUSE_EVENTS.includes(nsc)) pauseTimer();
    else continueTimer();
  }, [nsc, pauseTimer, continueTimer]);

  // OT
  useEffect(() => {
    if (nsc === "1281") {
      // Overtime Start
      setIsOvertime(true);
    } else if (nsc && ["1280", "1282", "1283", "1287"].includes(nsc)) {
      setIsOvertime(false);
    }
  }, [nsc]);

  const shouldHideTimer = !!(nsc && TIMER_HIDE_EVENTS.includes(nsc));
  const timerVars = useMemo(() => buildTimerCssVars(status), [status]);

  return (
    <div
      className={`absolute top-0 flex -translate-x-1/2 flex-col items-start justify-center mt-[0.5vw] ${
        isOvertime ? "left-[53%]" : "left-1/2"
      }`}
      style={timerVars}
    >
      <TwoTeamScoreBoard
        width="11vw"
        scoreHeight="6vw"
        accentHeight="1vw"
        gap="0.2vw"
        columnGap="0.2vw"
        homeAccentColor="var(--home-color)"
        awayAccentColor="var(--away-color)"
        homeContent={homeScore}
        awayContent={awayScore}
      />

      {!shouldHideTimer && (
        <div className="mt-[0.2vw] flex items-center justify-center gap-[0.2vw]">
          <div className="flex h-[4vw] w-[11vw] items-center justify-center bg-[rgba(0,0,0,0.8)] text-center font-bold text-white">
            <Text variant="iceHockeyTimer">
              <span>{minutes}</span>
              <span>:</span>
              <span>{String(seconds).padStart(2, "0")}</span>
            </Text>
          </div>

          {isOvertime && (
            <div className="flex h-[4vw] w-[11vw] items-center justify-center bg-[#ff0000] text-center font-bold text-white">
              <Text variant="iceHockeyTimer">OT</Text>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

const buildTimerCssVars = (rawStatus: MatchStatus): CSSProperties => {
  const { home, away } = getTeamColors(rawStatus as any);
  return {
    "--home-color": home,
    "--away-color": away,
  } as CSSProperties;
};
